import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Globe,
  LifeBuoy,
  Link2,
  Loader2,
  MessageSquareReply,
  RefreshCw,
  Star,
} from 'lucide-react'
import { MarketingShell } from './MarketingShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatRelative } from '@/lib/utils'
import { describeApiError } from '@/lib/apiError'
import { getStoredUserRole } from '@/lib/staff-roles'
import type {
  ExternalReviewRow,
  RecoveryState,
  ReputationGoogleLocation,
  ReputationGoogleStatus,
  ReputationSettings,
  ReviewMatchSuggestion,
} from '@/types'

const STATE_STYLES: Record<RecoveryState, string> = {
  new: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  acknowledged: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  recovered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  closed: 'bg-muted text-muted-foreground',
}

const STATE_LABELS: Record<RecoveryState, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  recovered: 'Recovered',
  closed: 'Closed',
}

const PAGE_SIZE = 50

/**
 * Reputation — live public Google reviews, pulled by the daily poller.
 *
 * The other half of the Reviews funnel: Reviews prevents bad public reviews
 * by catching unhappy guests privately; this tab shows the ones that got
 * through anyway, and hands a matched reviewer to the same recovery coupon
 * machinery. The inbox is admin+manager (shift work, like Guest recovery);
 * the settings card renders for admins only, mirroring the backend gate.
 */
export function AdminReputation() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const role = getStoredUserRole()
  const isAdmin = role === 'admin' || role === ''

  const [stateFilter, setStateFilter] = useState<'all' | RecoveryState>('all')
  const [ratingFilter, setRatingFilter] = useState<'all' | '2' | '3'>('all')
  const [pages, setPages] = useState(1)
  const [matching, setMatching] = useState<ExternalReviewRow | null>(null)
  const [recovering, setRecovering] = useState<ExternalReviewRow | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['marketing', 'reputation-settings'],
    queryFn: () => apiClient.getReputationSettings(),
    staleTime: 60_000,
    enabled: isAdmin,
    // Default refetch-on-focus is what picks the new connection up when
    // the admin returns from the Google consent tab.
  })
  const settingsData = settingsQuery.data?.data ?? null

  const listParams = useMemo(
    () => ({
      state: stateFilter === 'all' ? undefined : stateFilter,
      rating_max: ratingFilter === 'all' ? undefined : Number(ratingFilter),
      limit: PAGE_SIZE * pages,
    }),
    [stateFilter, ratingFilter, pages],
  )

  const inboxQuery = useQuery({
    queryKey: ['marketing', 'external-reviews', listParams],
    queryFn: () => apiClient.listExternalReviews(listParams),
    staleTime: 15_000,
    refetchInterval: 120_000,
  })

  const reviews: ExternalReviewRow[] = inboxQuery.data?.data?.reviews ?? []
  const stats = inboxQuery.data?.data?.stats
  const filtersActive = stateFilter !== 'all' || ratingFilter !== 'all'
  const mayHaveMore = reviews.length >= PAGE_SIZE * pages

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['marketing', 'external-reviews'] })
  }

  const setState = useMutation({
    mutationFn: (v: { id: string; state: 'acknowledged' | 'closed' }) =>
      apiClient.updateExternalReviewState(v.id, v.state),
    onSuccess: invalidateAll,
    onError: (err: unknown) => {
      toast({
        title: 'Could not update',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const pollNow = useMutation({
    mutationFn: () => apiClient.triggerReputationPoll(),
    onSuccess: (res) => {
      toast({ title: 'Refresh queued', description: res.message })
      // The poller picks the job up within its 5-minute tick.
      setTimeout(invalidateAll, 30_000)
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not refresh',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  return (
    <MarketingShell
      title="Reputation"
      description="Your live Google reviews, watched daily — new negatives alert you, and a matched guest gets the same apology + comeback coupon as private feedback."
      actions={
        isAdmin && settingsData?.settings.enabled ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => pollNow.mutate()}
            disabled={pollNow.isPending}
          >
            {pollNow.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            Check for new reviews
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {isAdmin && <ReputationSettingsCard query={settingsQuery} />}

        {stats && stats.total > 0 && (
          <div className="grid grid-cols-3 gap-3">
            <StatTile label="Reviews tracked" value={String(stats.total)} />
            <StatTile
              label="Average rating"
              value={stats.avg_rating ? stats.avg_rating.toFixed(2) : '—'}
            />
            <StatTile
              label="Negatives awaiting action"
              value={String(stats.open_negative)}
              tone={stats.open_negative > 0 ? 'bad' : 'good'}
            />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={ratingFilter}
            onValueChange={(v) => {
              setRatingFilter(v as typeof ratingFilter)
              setPages(1)
            }}
          >
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              <SelectItem value="2">2 stars and under</SelectItem>
              <SelectItem value="3">3 stars and under</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={stateFilter}
            onValueChange={(v) => {
              setStateFilter(v as typeof stateFilter)
              setPages(1)
            }}
          >
            <SelectTrigger className="h-8 w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="recovered">Recovered</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          {inboxQuery.isFetching && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          )}
        </div>

        {inboxQuery.isError ? (
          <div className="rounded-lg border border-destructive/50 p-4 text-sm text-destructive">
            Couldn’t load reviews: {describeApiError(inboxQuery.error).message}
          </div>
        ) : inboxQuery.isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            {filtersActive ? (
              <>
                <p>No reviews match these filters.</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setStateFilter('all')
                    setRatingFilter('all')
                    setPages(1)
                  }}
                >
                  Clear filters
                </Button>
              </>
            ) : isAdmin ? (
              <p>
                Nothing yet. Connect Google (or add your Maps listing) and turn
                on monitoring above — the first sync pulls your review history
                within a few minutes.
              </p>
            ) : (
              <p>Nothing yet — review monitoring hasn’t pulled anything.</p>
            )}
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {reviews.map((r) => (
                <li key={r.id} className="rounded-lg border border-border/80 bg-background p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`}
                          aria-hidden
                        />
                      ))}
                    </span>
                    <span className="font-medium">{r.author_name || 'A guest'}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Globe className="h-3 w-3" aria-hidden /> Google
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[r.state]}`}
                    >
                      {STATE_LABELS[r.state]}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {r.reviewed_at ? formatRelative(r.reviewed_at) : formatRelative(r.first_seen_at)}
                    </span>
                  </div>

                  {r.text && <p className="mt-1.5 text-sm">{r.text}</p>}

                  {r.owner_reply_text && (
                    <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                      <MessageSquareReply className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span>
                        Replied on Google: <span className="italic">{r.owner_reply_text}</span>
                      </span>
                    </p>
                  )}

                  {r.matched_customer_id ? (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Matched to <strong>{r.matched_customer_name}</strong>
                      {r.matched_customer_phone && ` (${r.matched_customer_phone})`}
                      {r.state !== 'recovered' && (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto px-1 py-0 text-xs"
                          onClick={() => setMatching(r)}
                        >
                          change
                        </Button>
                      )}
                    </p>
                  ) : null}

                  {r.state === 'recovered' && r.coupon_code && (
                    <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400">
                      Comeback code <strong>{r.coupon_code}</strong> issued.
                    </p>
                  )}

                  {r.state !== 'recovered' && r.state !== 'closed' && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        disabled={!r.matched_customer_id}
                        title={
                          r.matched_customer_id
                            ? undefined
                            : 'Match this reviewer to a guest first — Google only gives us their display name.'
                        }
                        onClick={() => setRecovering(r)}
                      >
                        <LifeBuoy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Apologize + send coupon
                      </Button>
                      {!r.matched_customer_id && (
                        <Button size="sm" variant="outline" onClick={() => setMatching(r)}>
                          <Link2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                          Match guest
                        </Button>
                      )}
                      {r.state === 'new' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setState.mutate({ id: r.id, state: 'acknowledged' })}
                        >
                          Mark acknowledged
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setState.mutate({ id: r.id, state: 'closed' })}
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {mayHaveMore && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPages((p) => p + 1)}
                  disabled={inboxQuery.isFetching}
                >
                  {inboxQuery.isFetching && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Load older reviews
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {matching && (
        <MatchGuestDialog
          row={matching}
          onClose={() => setMatching(null)}
          onDone={() => {
            setMatching(null)
            invalidateAll()
          }}
        />
      )}
      {recovering && (
        <RecoverReviewerDialog
          row={recovering}
          onClose={() => setRecovering(null)}
          onDone={() => {
            setRecovering(null)
            invalidateAll()
          }}
        />
      )}
    </MarketingShell>
  )
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'good' | 'bad'
}) {
  return (
    <div className="rounded-lg border border-border/80 bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={`mt-0.5 text-xl font-semibold ${
          tone === 'bad'
            ? 'text-rose-600 dark:text-rose-400'
            : tone === 'good'
              ? 'text-emerald-600 dark:text-emerald-400'
              : ''
        }`}
      >
        {value}
      </p>
    </div>
  )
}

/**
 * Settings card — admin only (the backend gate is the authority; this just
 * avoids rendering a form a manager can't submit). place_query + poll hour
 * drive vendor spend, alert phone/email decide who gets woken up.
 */
function ReputationSettingsCard({
  query,
}: {
  // Structural subset of the parent's useQuery result — keeps the prop
  // decoupled from TanStack's generics.
  query: {
    isLoading: boolean
    data?: { data?: { settings: ReputationSettings; google: ReputationGoogleStatus } }
  }
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [form, setForm] = useState<ReputationSettings | null>(null)
  const google = query.data?.data?.google

  useEffect(() => {
    if (query.data?.data?.settings && !form) setForm(query.data.data.settings)
  }, [query.data, form])

  const invalidateSettings = () =>
    queryClient.invalidateQueries({ queryKey: ['marketing', 'reputation-settings'] })

  const connect = useMutation({
    mutationFn: () => apiClient.startGoogleReputationConnect(),
    onSuccess: (res) => {
      const url = res.data?.auth_url
      if (url) {
        // The consent screen opens in a new tab; returning focus to this
        // one refetches the settings and picks the connection up.
        window.open(url, '_blank', 'noopener')
        toast({
          title: 'Continue in the Google tab',
          description: 'Sign in with the Google account that manages the restaurant’s Business Profile.',
        })
      }
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not start Google connect',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const disconnect = useMutation({
    mutationFn: () => apiClient.disconnectGoogleReputation(),
    onSuccess: () => {
      toast({ title: 'Google disconnected' })
      invalidateSettings()
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not disconnect',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const save = useMutation({
    mutationFn: (body: ReputationSettings) => apiClient.updateReputationSettings(body),
    onSuccess: () => {
      toast({ title: 'Reputation settings saved' })
      queryClient.invalidateQueries({ queryKey: ['marketing', 'reputation-settings'] })
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  if (query.isLoading || !form) {
    return (
      <div className="flex h-16 items-center justify-center rounded-lg border border-border/80">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border/80 bg-background p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Review monitoring</h2>
          <p className="text-xs text-muted-foreground">
            Pulls your public Google reviews once a day and alerts you about new
            negatives — WhatsApp first, email as fallback.
          </p>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(v) => setForm({ ...form, enabled: v })}
          aria-label="Enable review monitoring"
        />
      </div>

      {/* Source: the connected Business Profile wins; the Maps-link field
          only drives the scraper fallback. */}
      <div className="mt-3 rounded-md border border-border/60 p-3">
        {!google?.configured ? (
          <p className="text-xs text-muted-foreground">
            Google Business Profile connect isn’t enabled on this deployment
            yet — reviews are pulled from the Maps listing below instead.
          </p>
        ) : google.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              Google connected
            </span>
            <span>
              Monitoring <strong>{google.location_title || 'your listing'}</strong>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
            >
              Disconnect
            </Button>
          </div>
        ) : google.pending_selection ? (
          <GoogleLocationPicker onDone={invalidateSettings} />
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm">
              <p className="font-medium">Connect Google Business Profile</p>
              <p className="text-xs text-muted-foreground">
                The official (free) source — pulls every review and needs no
                Maps link. Sign in with the account that manages your listing.
              </p>
            </div>
            <Button
              size="sm"
              className="ml-auto"
              onClick={() => connect.mutate()}
              disabled={connect.isPending}
            >
              {connect.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Connect Google
            </Button>
          </div>
        )}
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {!google?.connected && (
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="rep-place">Google listing (fallback source)</Label>
            <Input
              id="rep-place"
              value={form.place_query}
              maxLength={300}
              onChange={(e) => setForm({ ...form, place_query: e.target.value })}
              placeholder="Paste your Google Maps link, or type “Restaurant name, City”"
            />
          </div>
        )}
        <div className="space-y-1">
          <Label>Alert on ratings at or below</Label>
          <Select
            value={String(form.alert_threshold)}
            onValueChange={(v) => setForm({ ...form, alert_threshold: Number(v) })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} star{n > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Daily check time</Label>
          <Select
            value={String(form.poll_hour)}
            onValueChange={(v) => setForm({ ...form, poll_hour: Number(v) })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 24 }, (_, h) => (
                <SelectItem key={h} value={String(h)}>
                  {String(h).padStart(2, '0')}:00
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="rep-phone">Alert WhatsApp number</Label>
          <Input
            id="rep-phone"
            value={form.owner_alert_phone}
            onChange={(e) => setForm({ ...form, owner_alert_phone: e.target.value })}
            placeholder="Blank = same number as Reviews alerts"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="rep-email">Alert email</Label>
          <Input
            id="rep-email"
            type="email"
            value={form.owner_alert_email}
            onChange={(e) => setForm({ ...form, owner_alert_email: e.target.value })}
            placeholder="Fallback when WhatsApp isn’t available"
          />
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={() => save.mutate(form)} disabled={save.isPending}>
          {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  )
}

/**
 * Shown when the connected Google account manages several listings — the
 * admin picks which one this tenant monitors. Choices come from the
 * candidates the connect round-trip stored; the backend refuses anything
 * outside that list.
 */
function GoogleLocationPicker({ onDone }: { onDone: () => void }) {
  const { toast } = useToast()
  const [selected, setSelected] = useState<ReputationGoogleLocation | null>(null)

  const locationsQuery = useQuery({
    queryKey: ['marketing', 'reputation-google-locations'],
    queryFn: () => apiClient.listGoogleReputationLocations(),
    staleTime: 60_000,
  })
  const candidates: ReputationGoogleLocation[] =
    locationsQuery.data?.data?.candidates ?? []

  const select = useMutation({
    mutationFn: (loc: ReputationGoogleLocation) =>
      apiClient.selectGoogleReputationLocation(loc.account, loc.location),
    onSuccess: (res) => {
      toast({ title: res.message || 'Listing selected' })
      onDone()
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save the selection',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  if (locationsQuery.isLoading) {
    return (
      <div className="flex h-12 items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        Google connected — which listing should we monitor?
      </p>
      <ul className="space-y-1.5">
        {candidates.map((l) => (
          <li key={l.account + l.location}>
            <button
              type="button"
              onClick={() => setSelected(l)}
              className={`w-full rounded-md border p-2 text-left text-sm transition-colors ${
                selected?.location === l.location && selected?.account === l.account
                  ? 'border-primary bg-primary/5'
                  : 'border-border/80 hover:bg-muted/50'
              }`}
            >
              <span className="font-medium">{l.title}</span>
              {l.address && (
                <span className="block text-xs text-muted-foreground">{l.address}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!selected || select.isPending}
          onClick={() => selected && select.mutate(selected)}
        >
          {select.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Monitor this listing
        </Button>
      </div>
    </div>
  )
}

/**
 * The match step. Google gives us a display name and nothing else, so the
 * link to a real guest is a human decision: fuzzy suggestions, owner
 * confirms. Recovery stays disabled until this happens — a coupon bound to
 * a guessed guest is worse than none.
 */
function MatchGuestDialog({
  row,
  onClose,
  onDone,
}: {
  row: ExternalReviewRow
  onClose: () => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const [selected, setSelected] = useState<string>(row.matched_customer_id ?? '')

  const suggestionsQuery = useQuery({
    queryKey: ['marketing', 'external-reviews', row.id, 'match-suggestions'],
    queryFn: () => apiClient.getExternalReviewMatchSuggestions(row.id),
    staleTime: 60_000,
  })
  const suggestions: ReviewMatchSuggestion[] =
    suggestionsQuery.data?.data?.suggestions ?? []

  const match = useMutation({
    mutationFn: (customerId: string) => apiClient.matchExternalReview(row.id, customerId),
    onSuccess: (_res, customerId) => {
      toast({ title: customerId ? 'Guest matched' : 'Match cleared' })
      onDone()
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not save the match',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Who is “{row.author_name || 'this reviewer'}”?</DialogTitle>
        <DialogDescription>
          Google only shares the reviewer’s display name. Confirm which guest
          this is so the apology and coupon reach a real person — if you’re not
          sure, don’t guess.
        </DialogDescription>

        {suggestionsQuery.isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : suggestions.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No customers with a similar name. Find them in Customers and check
            their visits — matching only works when they exist in your
            directory.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {suggestions.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSelected(s.id)}
                  className={`w-full rounded-md border p-2.5 text-left text-sm transition-colors ${
                    selected === s.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border/80 hover:bg-muted/50'
                  }`}
                >
                  <span className="font-medium">{s.display_name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {s.phone && `${s.phone} · `}
                    {s.visit_count} visit{s.visit_count === 1 ? '' : 's'}
                    {s.last_visit_at && ` · last ${formatRelative(s.last_visit_at)}`}
                    {s.whatsapp_opt_in && ' · WhatsApp ok'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-2 flex justify-end gap-2">
          {row.matched_customer_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => match.mutate('')}
              disabled={match.isPending}
            >
              Clear match
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose} disabled={match.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!selected || match.isPending}
            onClick={() => match.mutate(selected)}
          >
            {match.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Confirm match
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The confirm step — same tier presets, override cap and reason vocabulary
 * as guest-feedback recovery (`recovery_coupon_config`), so a coupon issued
 * here and one issued at the till are the same object with the same limits.
 */
function RecoverReviewerDialog({
  row,
  onClose,
  onDone,
}: {
  row: ExternalReviewRow
  onClose: () => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const [tier, setTier] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [sendMessage, setSendMessage] = useState(true)

  const configQuery = useQuery({
    queryKey: ['recovery-coupon-config'],
    queryFn: () => apiClient.getRecoveryCouponConfig(),
    staleTime: 300_000,
  })
  const cfg = configQuery.data?.data ?? null

  const ledgerReason = [reason, note.trim()].filter(Boolean).join(' — ')

  const recover = useMutation({
    mutationFn: () =>
      apiClient.recoverExternalReview(row.id, {
        tier,
        reason: ledgerReason || 'Public review follow-up',
        send_message: sendMessage,
      }),
    onSuccess: (res) => {
      const channel = res.data?.message_channel
      if (channel === 'whatsapp_click' && res.data?.wa_deeplink) {
        window.open(res.data.wa_deeplink, '_blank', 'noopener')
      }
      toast({
        title: `Coupon ${res.data?.code} issued`,
        description:
          channel === 'none'
            ? 'No contact method on file — reach the guest yourself with the code.'
            : channel === 'whatsapp_click'
              ? 'WhatsApp opened — send the message to finish.'
              : `Apology queued over ${channel === 'whatsapp_api' ? 'WhatsApp' : 'email'}.`,
      })
      onDone()
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not issue the coupon',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const disabled = !cfg?.enabled || !tier || recover.isPending

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>
          Recover {row.matched_customer_name || row.author_name || 'this guest'}
        </DialogTitle>
        <DialogDescription>
          Issues a single-use comeback code bound to the matched guest — it
          can’t be shared or used by anyone else. Their Google review is not
          touched; win them back first, then ask for an update.
        </DialogDescription>

        {configQuery.isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !cfg?.enabled ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Recovery coupons are switched off. Turn them on in Settings →
            Recovery coupons to use this.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>How bad was it?</Label>
              <div className="grid grid-cols-3 gap-2">
                {cfg.tiers.map((t) => (
                  <Button
                    key={t.key}
                    type="button"
                    variant={tier === t.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTier(t.key)}
                  >
                    {t.label} · {t.percent}%
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Pick a reason" />
                </SelectTrigger>
                <SelectContent>
                  {cfg.reasons.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="xr-note">Internal note</Label>
              <Textarea
                id="xr-note"
                rows={2}
                maxLength={160}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional — appended to the reason on the audit ledger."
              />
            </div>

            <label className="flex items-start gap-2 text-sm">
              <Switch checked={sendMessage} onCheckedChange={setSendMessage} />
              <span>
                Send the apology now
                <span className="block text-xs text-muted-foreground">
                  WhatsApp if the guest opted in, otherwise email, otherwise a
                  WhatsApp link for you to send yourself. Off = issue the code
                  quietly and ring them instead.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={recover.isPending}>
            Cancel
          </Button>
          <Button size="sm" disabled={disabled} onClick={() => recover.mutate()}>
            {recover.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Issue coupon
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
